// Phase COLLEGE-PRICING-1 · Admin/Founder CMS · /dashboard/{admin|founder}/college-pricing
//
// Layout: header bar with version dropdown + actions · left editor (page + sortable
// cards via HTML5 drag) · right live preview using the SAME public component.
// Sticky bottom action bar shows unsaved/dirty + Save/Discard/Publish.
// Drawers: Version History (with diff), Audit Log.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Plus, Trash2, History, RotateCcw, Eye, Smartphone, Monitor, GripVertical, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import RoleGate from "../../components/RoleGate";
import { CollegePricingPageView } from "../../components/college/CollegePricingView";

const ORANGE = "#FF6B00";
const BUILD_STAMP = "APP-1.0-r7";
const ICONS = ["suitcase", "duffel", "box", "boxes", "truck", "custom_url"];

function newCard(order_index) {
  return {
    card_id: uuidv4(),
    order_index,
    title: "New Card",
    subtitle: "",
    price_prefix: "₹",
    price_value: "0",
    price_suffix: "",
    footer_tagline: "",
    whatsapp_button_text: "Book on WhatsApp",
    whatsapp_number: "917995865243",
    whatsapp_prefilled_message: "Hi PickNGo",
    icon: "box",
    icon_url: null,
    border_color: "#E5E7EB",
    is_most_popular: false,
    active: true,
  };
}

function deepEqual(a, b) { return JSON.stringify(a) === JSON.stringify(b); }

function Body() {
  const [draft, setDraft] = useState(null);          // {version_id, page, cards}
  const [serverDraft, setServerDraft] = useState(null);
  const [activePub, setActivePub] = useState(null);  // {version_id, page, cards} OR null
  const [activePointer, setActivePointer] = useState(null);
  const [versions, setVersions] = useState([]);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [publishChecked, setPublishChecked] = useState(false);
  const dragSrcIdx = useRef(null);

  const dirty = useMemo(() =>
    !!(draft && serverDraft) && !deepEqual({ page: draft.page, cards: draft.cards },
                                              { page: serverDraft.page, cards: serverDraft.cards }),
    [draft, serverDraft]);

  // ─── Bootstrap ──────────────────────────────────────────
  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const a = await api.get("/admin/college-pricing/active");
      setActivePub(a.data?.published || null);
      setActivePointer(a.data?.active || null);
      const v = await api.get("/admin/college-pricing/versions?status=all&limit=20");
      setVersions(v.data?.versions || []);
      const existing = (v.data?.versions || []).find((vv) => vv.status === "draft");
      if (existing) {
        const full = await api.get(`/admin/college-pricing/versions/${existing.version_id}`);
        setDraft(full.data);
        setServerDraft(full.data);
      } else {
        setDraft(null); setServerDraft(null);
      }
    } catch (e) {
      toast.error(`Load failed · ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const createDraft = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/college-pricing/drafts", {});
      setDraft(r.data.version); setServerDraft(r.data.version);
      toast.success(`Draft ${r.data.version.version_id} ready`);
      reload();
    } catch (e) {
      toast.error(`Create draft failed · ${e?.response?.data?.detail || e?.message}`);
    } finally { setBusy(false); }
  };

  const saveDraft = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const r = await api.patch(`/admin/college-pricing/drafts/${draft.version_id}`, {
        page: draft.page, cards: draft.cards,
      });
      setDraft(r.data.version); setServerDraft(r.data.version);
      toast.success("Draft saved");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "object" ? JSON.stringify(detail) : (detail || e?.message);
      toast.error(`Save failed · ${msg}`);
    } finally { setBusy(false); }
  };

  const discard = async () => {
    if (serverDraft) { setDraft(serverDraft); toast("Local changes discarded"); }
  };

  const publish = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const expected = activePointer?.current_version_id || null;
      await api.post(`/admin/college-pricing/drafts/${draft.version_id}/publish`,
                      { expected_version_id: expected });
      toast.success(`Published ${draft.version_id}`);
      setConfirmPublish(false); setPublishChecked(false);
      reload();
    } catch (e) {
      toast.error(`Publish failed · ${e?.response?.data?.detail?.code || e?.response?.data?.detail || e?.message}`);
    } finally { setBusy(false); }
  };

  const openDiff = async (sel_vid) => {
    if (!activePointer?.current_version_id) return;
    const r = await api.get(`/admin/college-pricing/diff?from=${activePointer.current_version_id}&to=${sel_vid}`);
    setDiffData(r.data);
  };

  const restoreFromVersion = async (vid) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/college-pricing/versions/${vid}/restore`, {});
      setDraft(r.data.version); setServerDraft(r.data.version);
      toast.success(`Restored as draft ${r.data.version.version_id}`);
      setHistoryOpen(false); reload();
    } catch (e) {
      toast.error(`Restore failed · ${e?.response?.data?.detail?.code || e?.response?.data?.detail || e?.message}`);
    } finally { setBusy(false); }
  };

  // ─── Card mutators (local only; saved on PATCH) ─────────
  const updateCard = (idx, patch) =>
    setDraft((d) => ({ ...d, cards: d.cards.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));
  const setPopular = (idx) =>
    setDraft((d) => ({ ...d, cards: d.cards.map((c, i) => ({ ...c, is_most_popular: i === idx })) }));
  const removeCard = (idx) =>
    setDraft((d) => {
      const next = d.cards.filter((_, i) => i !== idx).map((c, i) => ({ ...c, order_index: i }));
      return { ...d, cards: next };
    });
  const addCard = () =>
    setDraft((d) => ({ ...d, cards: [...d.cards, newCard(d.cards.length)] }));

  // HTML5 drag-drop reorder (no extra dep).
  const onDragStart = (idx) => { dragSrcIdx.current = idx; };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (targetIdx) => {
    if (dragSrcIdx.current === null || dragSrcIdx.current === targetIdx) return;
    const src = dragSrcIdx.current;
    setDraft((d) => {
      const arr = d.cards.slice();
      const [moved] = arr.splice(src, 1);
      arr.splice(targetIdx, 0, moved);
      return { ...d, cards: arr.map((c, i) => ({ ...c, order_index: i })) };
    });
    dragSrcIdx.current = null;
  };

  if (busy && !draft && !activePub) {
    return <div className="p-10 text-center text-slate-500"
                  data-testid="cp-cms-loading">Loading…</div>;
  }

  return (
    <div className="min-h-[100dvh] bg-slate-50" data-testid="college-pricing-cms-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex flex-wrap items-center gap-2 sticky top-0 z-30">
        <h1 className="text-lg font-bold text-slate-900 flex-1">College Pricing CMS</h1>
        <select value={draft?.version_id || ""} onChange={(e) => {
          const vid = e.target.value;
          if (!vid) return;
          api.get(`/admin/college-pricing/versions/${vid}`)
                .then((r) => { setDraft(r.data); setServerDraft(r.data); });
        }} data-testid="cp-cms-version-select"
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5">
          <option value="">(pick version)</option>
          {versions.map((v) => (
            <option key={v.version_id} value={v.version_id}>
              {v.version_id} · {v.status}{v.published_at ? " · " + v.published_at.slice(0, 10) : ""}
            </option>
          ))}
        </select>
        <button onClick={() => setHistoryOpen(true)}
                data-testid="cp-cms-history-btn"
                className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
          <History className="h-3 w-3"/> History
        </button>
        <button onClick={() => setAuditOpen(true)}
                data-testid="cp-cms-audit-btn"
                className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50">
          Audit
        </button>
        <span className="text-[10px] font-mono text-slate-400">{BUILD_STAMP}</span>
      </header>

      {/* No draft state */}
      {!draft && (
        <div className="max-w-2xl mx-auto p-8 text-center" data-testid="cp-cms-no-draft">
          <p className="text-sm text-slate-600 mb-3">
            No active draft. Click below to clone the published version into a new draft and start editing.
          </p>
          <button onClick={createDraft}
                  data-testid="cp-cms-create-draft-btn"
                  className="text-sm font-semibold px-4 py-2 rounded-lg text-white"
                  style={{ backgroundColor: ORANGE }}>
            Create new draft
          </button>
        </div>
      )}

      {/* Editor + preview */}
      {draft && (
        <div className="grid lg:grid-cols-5 gap-4 p-4 pb-24 max-w-[1600px] mx-auto">
          {/* Editor */}
          <div className="lg:col-span-3 space-y-4">
            <section className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" data-testid="cp-cms-page-fields">
              <h2 className="text-sm font-bold text-slate-900">Page</h2>
              <label className="block text-xs">
                <span className="text-slate-500 uppercase tracking-wider text-[10px]">Heading *</span>
                <input value={draft.page?.heading || ""}
                       onChange={(e) => setDraft({ ...draft, page: { ...draft.page, heading: e.target.value } })}
                       data-testid="cp-cms-page-heading"
                       className="w-full mt-0.5 border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
              </label>
              <label className="block text-xs">
                <span className="text-slate-500 uppercase tracking-wider text-[10px]">Subheading</span>
                <textarea value={draft.page?.subheading || ""}
                          onChange={(e) => setDraft({ ...draft, page: { ...draft.page, subheading: e.target.value } })}
                          data-testid="cp-cms-page-subheading"
                          rows={2}
                          className="w-full mt-0.5 border border-slate-200 rounded-lg px-2 py-1.5 text-sm resize-none"/>
              </label>
              <label className="block text-xs">
                <span className="text-slate-500 uppercase tracking-wider text-[10px]">Highlight</span>
                <input value={draft.page?.highlight || ""}
                       onChange={(e) => setDraft({ ...draft, page: { ...draft.page, highlight: e.target.value } })}
                       data-testid="cp-cms-page-highlight"
                       className="w-full mt-0.5 border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs inline-flex items-center gap-2">
                  <input type="checkbox" checked={!!draft.page?.most_popular_enabled}
                         onChange={(e) => setDraft({ ...draft, page: { ...draft.page, most_popular_enabled: e.target.checked } })}
                         data-testid="cp-cms-popular-enabled"/>
                  Most-popular badge enabled
                </label>
                <label className="block text-xs">
                  <span className="text-slate-500 uppercase tracking-wider text-[10px]">Badge text</span>
                  <input value={draft.page?.most_popular_badge_text || ""}
                         onChange={(e) => setDraft({ ...draft, page: { ...draft.page, most_popular_badge_text: e.target.value } })}
                         data-testid="cp-cms-popular-badge-text"
                         className="w-full mt-0.5 border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
                </label>
              </div>
            </section>

            <section className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="cp-cms-cards">
              <div className="flex items-center mb-3">
                <h2 className="text-sm font-bold text-slate-900 flex-1">Cards · {draft.cards.length}/10</h2>
                <button onClick={addCard}
                        disabled={draft.cards.length >= 10}
                        data-testid="cp-cms-add-card"
                        className="text-xs font-semibold px-2 py-1 rounded-lg text-white inline-flex items-center gap-1 disabled:opacity-40"
                        style={{ backgroundColor: ORANGE }}>
                  <Plus className="h-3 w-3"/> Add card
                </button>
              </div>
              <div className="space-y-3">
                {draft.cards.map((c, idx) => (
                  <div key={c.card_id}
                       draggable
                       onDragStart={() => onDragStart(idx)}
                       onDragOver={onDragOver}
                       onDrop={() => onDrop(idx)}
                       data-testid={`cp-cms-card-row-${c.card_id}`}
                       data-order-index={idx}
                       className="border border-slate-200 rounded-xl p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-slate-400 cursor-move shrink-0"
                                       data-testid={`cp-cms-card-drag-${c.card_id}`}/>
                      <input value={c.title}
                             onChange={(e) => updateCard(idx, { title: e.target.value })}
                             data-testid={`cp-cms-card-${c.card_id}-title`}
                             className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
                      <button onClick={() => removeCard(idx)}
                              data-testid={`cp-cms-card-${c.card_id}-delete`}
                              className="text-rose-500 hover:bg-rose-50 p-1 rounded">
                        <Trash2 className="h-4 w-4"/>
                      </button>
                    </div>
                    <input value={c.subtitle}
                           onChange={(e) => updateCard(idx, { subtitle: e.target.value })}
                           placeholder="Subtitle"
                           data-testid={`cp-cms-card-${c.card_id}-subtitle`}
                           className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
                    <div className="grid grid-cols-3 gap-2">
                      <input value={c.price_prefix}
                             onChange={(e) => updateCard(idx, { price_prefix: e.target.value })}
                             placeholder="₹"
                             data-testid={`cp-cms-card-${c.card_id}-price-prefix`}
                             className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
                      <input value={c.price_value}
                             onChange={(e) => updateCard(idx, { price_value: e.target.value })}
                             placeholder="999"
                             data-testid={`cp-cms-card-${c.card_id}-price-value`}
                             className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono"/>
                      <input value={c.price_suffix}
                             onChange={(e) => updateCard(idx, { price_suffix: e.target.value })}
                             placeholder="onwards"
                             data-testid={`cp-cms-card-${c.card_id}-price-suffix`}
                             className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
                    </div>
                    <input value={c.footer_tagline}
                           onChange={(e) => updateCard(idx, { footer_tagline: e.target.value })}
                           placeholder="Footer tagline"
                           data-testid={`cp-cms-card-${c.card_id}-tagline`}
                           className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
                    <div className="grid grid-cols-2 gap-2">
                      <input value={c.whatsapp_number}
                             onChange={(e) => updateCard(idx, { whatsapp_number: e.target.value })}
                             placeholder="917995865243"
                             data-testid={`cp-cms-card-${c.card_id}-wa-number`}
                             className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono"/>
                      <input value={c.whatsapp_button_text}
                             onChange={(e) => updateCard(idx, { whatsapp_button_text: e.target.value })}
                             placeholder="Book on WhatsApp"
                             data-testid={`cp-cms-card-${c.card_id}-wa-text`}
                             className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
                    </div>
                    <textarea value={c.whatsapp_prefilled_message}
                              onChange={(e) => updateCard(idx, { whatsapp_prefilled_message: e.target.value })}
                              placeholder="Prefilled WhatsApp message"
                              rows={2}
                              data-testid={`cp-cms-card-${c.card_id}-wa-msg`}
                              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm resize-none"/>
                    <div className="grid grid-cols-4 gap-2 items-center">
                      <select value={c.icon}
                              onChange={(e) => updateCard(idx, { icon: e.target.value })}
                              data-testid={`cp-cms-card-${c.card_id}-icon`}
                              className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
                        {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
                      </select>
                      <input type="color" value={c.border_color}
                             onChange={(e) => updateCard(idx, { border_color: e.target.value.toUpperCase() })}
                             data-testid={`cp-cms-card-${c.card_id}-color`}
                             className="w-full h-9 border border-slate-200 rounded-lg cursor-pointer"/>
                      <label className="inline-flex items-center gap-1 text-xs">
                        <input type="radio" name="popular"
                               checked={c.is_most_popular}
                               onChange={() => setPopular(idx)}
                               data-testid={`cp-cms-card-${c.card_id}-popular`}/>
                        Popular
                      </label>
                      <label className="inline-flex items-center gap-1 text-xs">
                        <input type="checkbox" checked={c.active}
                               onChange={(e) => updateCard(idx, { active: e.target.checked })}
                               data-testid={`cp-cms-card-${c.card_id}-active`}/>
                        Active
                      </label>
                    </div>
                    {c.icon === "custom_url" && (
                      <input value={c.icon_url || ""}
                             onChange={(e) => updateCard(idx, { icon_url: e.target.value })}
                             placeholder="https://… icon image url"
                             data-testid={`cp-cms-card-${c.card_id}-icon-url`}
                             className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Preview */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden sticky top-20"
                 data-testid="cp-cms-preview-panel">
              <div className="px-3 py-2 border-b border-slate-200 flex gap-1 text-xs">
                <button onClick={() => setPreviewMode("desktop")}
                        data-testid="cp-cms-preview-desktop"
                        data-active={previewMode === "desktop" ? "true" : "false"}
                        className={`px-2 py-1 rounded ${previewMode === "desktop" ? "bg-slate-900 text-white" : "text-slate-600"}`}>
                  <Monitor className="h-3 w-3 inline mr-1"/> Desktop
                </button>
                <button onClick={() => setPreviewMode("mobile")}
                        data-testid="cp-cms-preview-mobile"
                        data-active={previewMode === "mobile" ? "true" : "false"}
                        className={`px-2 py-1 rounded ${previewMode === "mobile" ? "bg-slate-900 text-white" : "text-slate-600"}`}>
                  <Smartphone className="h-3 w-3 inline mr-1"/> Mobile (375)
                </button>
                <a href="/college-logistics-pricing" target="_blank" rel="noopener noreferrer"
                   className="ml-auto text-xs text-[#FF6B00] hover:underline inline-flex items-center gap-1"
                   data-testid="cp-cms-open-public">
                  <Eye className="h-3 w-3"/> Live page
                </a>
              </div>
              <div className={`overflow-auto ${previewMode === "mobile" ? "max-w-[375px] mx-auto" : ""}`}
                   data-testid="cp-cms-preview-frame"
                   data-preview-mode={previewMode}>
                <CollegePricingPageView page={draft.page} cards={draft.cards} variant={previewMode}/>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom action bar */}
      {draft && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 px-4 py-2 flex items-center gap-2 z-40"
             data-testid="cp-cms-action-bar">
          <span data-testid="cp-cms-dirty-indicator"
                data-dirty={dirty ? "true" : "false"}
                className={`text-xs ${dirty ? "text-rose-600" : "text-slate-500"}`}>
            {dirty ? "Unsaved changes" : "All saved"}
          </span>
          <span className="text-xs text-slate-400 ml-2">{draft.version_id} · draft</span>
          <button onClick={discard} disabled={!dirty || busy}
                  data-testid="cp-cms-discard-btn"
                  className="ml-auto text-xs px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40">
            Discard
          </button>
          <button onClick={saveDraft} disabled={busy}
                  data-testid="cp-cms-save-btn"
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 inline-flex items-center gap-1">
            {busy && <Loader2 className="h-3 w-3 animate-spin"/>} Save Draft
          </button>
          <button onClick={() => setConfirmPublish(true)} disabled={busy || dirty}
                  data-testid="cp-cms-publish-btn"
                  title={dirty ? "Save your draft first" : ""}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-40 inline-flex items-center gap-1"
                  style={{ backgroundColor: ORANGE }}>
            Publish
          </button>
        </div>
      )}

      {/* Publish modal */}
      {confirmPublish && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
             onClick={(e) => { if (e.target === e.currentTarget) setConfirmPublish(false); }}
             data-testid="cp-cms-publish-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-3">
            <h3 className="text-lg font-bold text-slate-900">Publish {draft?.version_id}?</h3>
            <p className="text-xs text-slate-600">
              This will replace the live page (currently {activePointer?.current_version_id || "—"}).
              The current published version becomes archived. This is atomic.
            </p>
            <label className="text-xs inline-flex items-start gap-2">
              <input type="checkbox" checked={publishChecked}
                     onChange={(e) => setPublishChecked(e.target.checked)}
                     data-testid="cp-cms-publish-confirm-checkbox"
                     className="mt-0.5"/>
              I confirm publishing replaces the live page.
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setConfirmPublish(false); setPublishChecked(false); }}
                      data-testid="cp-cms-publish-cancel"
                      className="text-xs px-3 py-1.5 rounded-lg border border-slate-200">
                Cancel
              </button>
              <button onClick={publish} disabled={!publishChecked || busy}
                      data-testid="cp-cms-publish-now"
                      className="text-xs font-semibold px-4 py-1.5 rounded-lg text-white disabled:opacity-40"
                      style={{ backgroundColor: ORANGE }}>
                Publish Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History drawer */}
      {historyOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 flex justify-end"
             onClick={(e) => { if (e.target === e.currentTarget) setHistoryOpen(false); }}
             data-testid="cp-cms-history-drawer">
          <div className="bg-white w-full max-w-md h-full overflow-y-auto p-4 space-y-3">
            <div className="flex items-center">
              <h3 className="text-lg font-bold text-slate-900 flex-1">Version History</h3>
              <button onClick={() => setHistoryOpen(false)} className="p-1 rounded hover:bg-slate-100"
                      data-testid="cp-cms-history-close">
                <X className="h-4 w-4"/>
              </button>
            </div>
            <ul className="space-y-2">
              {versions.map((v) => (
                <li key={v.version_id}
                    data-testid={`cp-cms-history-row-${v.version_id}`}
                    className="border border-slate-200 rounded-lg p-2 text-xs">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold">{v.version_id}</span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] uppercase"
                          style={{ backgroundColor:
                              v.status === "published" ? "#16A34A" :
                              v.status === "draft" ? "#F59E0B" : "#94A3B8",
                            color: "white" }}>
                      {v.status}
                    </span>
                    <span className="text-slate-500">
                      {(v.published_at || v.created_at || "").slice(0, 16).replace("T", " ")}
                    </span>
                    <span className="text-slate-400 ml-auto">{v.created_by_email || "—"}</span>
                  </div>
                  <div className="mt-1 flex gap-2">
                    <button onClick={() => openDiff(v.version_id)}
                            data-testid={`cp-cms-history-diff-${v.version_id}`}
                            className="text-[11px] hover:underline">Diff vs active</button>
                    {v.status !== "draft" && (
                      <button onClick={() => restoreFromVersion(v.version_id)}
                              data-testid={`cp-cms-history-restore-${v.version_id}`}
                              className="text-[11px] text-[#FF6B00] hover:underline inline-flex items-center gap-1">
                        <RotateCcw className="h-3 w-3"/> Restore as draft
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            {diffData && (
              <div className="mt-3 border-t border-slate-200 pt-3" data-testid="cp-cms-diff-panel">
                <h4 className="text-sm font-bold mb-2">Diff · {diffData.from} → {diffData.to}</h4>
                <pre className="text-[10px] bg-slate-50 p-2 rounded overflow-x-auto whitespace-pre-wrap">
                  {JSON.stringify(diffData, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit drawer */}
      {auditOpen && draft && (
        <AuditDrawer versionId={draft.version_id} onClose={() => setAuditOpen(false)}/>
      )}
    </div>
  );
}

function AuditDrawer({ versionId, onClose }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/admin/college-pricing/audit?version_id=${versionId}&limit=100`)
        .then((r) => setRows(r.data.rows || []))
        .catch(() => setRows([]));
  }, [versionId]);
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="cp-cms-audit-drawer">
      <div className="bg-white w-full max-w-md h-full overflow-y-auto p-4 space-y-2">
        <div className="flex items-center">
          <h3 className="text-lg font-bold text-slate-900 flex-1">Audit · {versionId}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"
                  data-testid="cp-cms-audit-close">
            <X className="h-4 w-4"/>
          </button>
        </div>
        <ul className="space-y-2 text-xs">
          {rows.map((r) => (
            <li key={r.id} className="border border-slate-200 rounded-lg p-2"
                data-testid={`cp-cms-audit-row-${r.id}`}>
              <div className="font-semibold">{r.action}</div>
              <div className="text-slate-500">{r.actor_email || r.actor_user_id || "—"} · {(r.at || "").slice(0, 16).replace("T", " ")}</div>
              {r.field && <div className="font-mono text-[10px]">{r.field}</div>}
              {r.before !== undefined && r.after !== undefined && (
                <pre className="text-[10px] bg-slate-50 p-1 rounded mt-1 overflow-x-auto">
                  {JSON.stringify({ before: r.before, after: r.after }, null, 2)}
                </pre>
              )}
            </li>
          ))}
          {!rows.length && <li className="text-slate-500">No audit rows for this version yet.</li>}
        </ul>
      </div>
    </div>
  );
}

export default function CollegePricingCMS() {
  return (
    <RoleGate allow={["admin", "founder"]}>
      <Body/>
    </RoleGate>
  );
}
